import { useEffect, useState } from "react";

import type { PortSignal, SignalColour } from "@nav-games/pilotage";

const FILL: Record<SignalColour, string> = {
  red: "#c0382b",
  green: "#3aa055",
  yellow: "#fbbf24",
  white: "#f5f5f5",
};

interface Props {
  signal: PortSignal;
  /// Display height in pixels for a 3-light mast (which is every
  /// IPTS signal). Aspect ratio is preserved.
  size?: number;
}

/// Animated International Port Traffic Signal mast. Renders a
/// 3-light vertical mast with one circular bulb per `signal.lights`
/// slot. Supplementary signals additionally carry a yellow
/// `auxLight` on a small side bracket to the LEFT of the mast,
/// level with the top main light (per IALA R0111 Edition 1.3
/// Table 2 — Additional Signals and Messages).
///
/// When `characteristic === "flashing"` all lit bulbs (main + aux)
/// blink in unison at 60 fpm — the rate spec'd for IPTS signal 1.
/// Fixed signals stay continuously on; the "slow occulting"
/// variant the spec also allows is not animated separately here.
export function SignalStack({ signal, size = 120 }: Props) {
  const [on, setOn] = useState(true);

  useEffect(() => {
    if (signal.characteristic !== "flashing") {
      setOn(true);
      return;
    }
    const t = setInterval(() => setOn((v) => !v), 500);
    return () => clearInterval(t);
  }, [signal.characteristic]);

  const cellCount = signal.lights.length;
  const hasAux = signal.auxLight !== undefined;

  // Geometry (viewBox units):
  //   cellTop      — y of the first light centre
  //   cellSpacing  — vertical gap between bulb centres
  //   mainCx       — x of the main mast (right column when aux is
  //                  present, otherwise centred)
  //   auxCx        — x of the aux side lamp (only used when hasAux)
  const cellTop = 24;
  const cellSpacing = 22;
  const lastLightY = cellTop + (cellCount - 1) * cellSpacing;
  const poleBase = lastLightY + 22;
  const h = poleBase + 6;
  // Reference viewBox width: 60 if no aux, 96 if aux present (so
  // the two columns share the canvas evenly).
  const w = hasAux ? 96 : 60;
  const mainCx = hasAux ? 66 : 30;
  const auxCx = 24;
  // Match the 3-light reference height when sizing in px so the
  // caller's `size` prop is consistent regardless of cellCount.
  const referenceH = 24 + (3 - 1) * 22 + 22 + 6; // 96
  const pxH = Math.round((size * h) / referenceH);
  const pxW = Math.round((pxH * w) / h);

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={pxW}
      height={pxH}
      style={{ display: "block", margin: "0 auto" }}
      role="img"
      aria-label={signal.shortMeaning}
    >
      {/* Main pole + plinth */}
      <line
        x1={mainCx}
        y1={poleBase}
        x2={mainCx}
        y2={cellTop - 12}
        stroke="#1c2530"
        strokeWidth="2"
      />
      <rect
        x={mainCx - 10}
        y={poleBase}
        width="20"
        height="6"
        fill="#1c2530"
      />

      {/* Main lights, top to bottom */}
      {signal.lights.map((c, i) => {
        const cy = cellTop + i * cellSpacing;
        return <Bulb key={i} cx={mainCx} cy={cy} colour={c} lit={on} />;
      })}

      {/* Auxiliary side lamp — bracket arm from main pole to the
          aux mounting, then a single bulb level with the top main
          light. Only present on supplementary signals. */}
      {hasAux && signal.auxLight && (
        <>
          {/* Bracket arm from main pole into the aux housing */}
          <line
            x1={mainCx - 16}
            y1={cellTop}
            x2={auxCx + 16}
            y2={cellTop}
            stroke="#1c2530"
            strokeWidth="1.5"
          />
          <Bulb cx={auxCx} cy={cellTop} colour={signal.auxLight} lit={on} />
        </>
      )}
    </svg>
  );
}

function Bulb({
  cx,
  cy,
  colour,
  lit,
}: {
  cx: number;
  cy: number;
  colour: SignalColour;
  lit: boolean;
}) {
  return (
    <g>
      <rect
        x={cx - 16}
        y={cy - 10}
        width="32"
        height="20"
        fill="#1c2530"
        rx="3"
      />
      <circle
        cx={cx}
        cy={cy}
        r="6.5"
        fill={lit ? FILL[colour] : "#2a3340"}
        stroke="#000"
        strokeWidth="0.6"
        style={{ transition: "fill 120ms linear" }}
      />
    </g>
  );
}
